import React, { useState, useRef, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { array, object, bool, func, string, number, oneOfType } from 'prop-types'
import { stripUnit } from 'polished'

import { white, greyLight, black } from 'styles/colors'
import { borderRadius } from 'styles/global'
import * as spacing from 'styles/spacing'
import { media } from 'styles/media'
import { fontSizes, fontWeights } from 'styles/typography'

import Icon from 'components/Icon'
import DownChevronLarge from 'icons/down-chevron-large.svg'

const Container = styled.div`
  position: static;
`

const Wrap = styled.div`
  position: relative;
`

const Inner = styled.div`
  position: relative;
  display: block;
  border-radius: ${borderRadius};
  background-color: ${white};
  box-shadow: 0px 2px 21px rgba(0, 0, 0, 0.08);
`

const Label = styled.span`
  position: absolute;
  color: ${black};
  pointer-events: none;
  font-size: ${fontSizes.tiny};
  left: ${spacing.xxLarge};
  top: ${stripUnit(spacing.small) * 1.5 + 'px'};

  ${media.large`
    left: ${spacing.xxxLarge};
  `};

  ${props =>
    props.isCompact &&
    css`
      font-size: 9px;
      left: ${spacing.large} !important;
    `}
`

const Trigger = styled.button`
  display: block;
  border-radius: 0;
  background-color: ${white};
  width: 100%;
  text-align: left;
  resize: none;
  color: ${black};
  vertical-align: top;
  outline: 0;
  padding-top: ${spacing.xLarge};
  padding-left: ${spacing.xxLarge};
  padding-right: ${spacing.xxLarge};
  padding-bottom: ${spacing.medium};
  cursor: pointer;

  ${media.large`
    padding-left: ${spacing.xxxLarge};
    padding-right: ${spacing.xxxLarge};
  `};

  ${props =>
    props.isCompact &&
    css`
      padding-left: ${spacing.large} !important;
      padding-right: ${spacing.large} !important;
      padding-top: ${spacing.large};
      padding-bottom: ${spacing.small};
    `}
`

const IconWrap = styled.div`
  position: absolute;
  top: 50%;
  right: ${spacing.xxLarge};
  transform: translateY(-50%);
  pointer-events: none;
  display: flex;
  color: ${greyLight};

  ${media.large`
    right: ${spacing.xxxLarge};
  `};

  ${props =>
    props.isCompact &&
    css`
      right: ${spacing.large} !important;
    `}
`

const Chevron = styled.div`
  display: flex;
  transform: ${props => (props.flipped ? 'rotate(180deg)' : 'none')};
`

const Menu = styled.div`
  padding: ${spacing.large};
  background-color: ${white};
  position: absolute;
  z-index: 10;
  top: 100%;
  left: 0;
  width: 100%;
  box-shadow: 0px 2px 21px rgba(0, 0, 0, 0.08);
  border-radius: ${borderRadius};
  padding-left: ${spacing.xxLarge};
  padding-right: ${spacing.xxLarge};
  margin-top: ${spacing.small};
  opacity: 0;
  visibility: hidden;

  ${props =>
    props.expanded &&
    css`
      opacity: 1;
      visibility: visible;
    `}

  ${media.large`
    padding-left: ${spacing.xxxLarge};
    padding-right: ${spacing.xxxLarge};
    margin-top: ${spacing.large};
  `};

  ${props =>
    props.isCompact &&
    css`
      padding-left: ${spacing.large} !important;
    `}
`

const MenuItem = styled.button`
  color: ${black};
  display: block;
  text-align: left;
  width: 100%;

  & + & {
    margin-top: ${spacing.small};

    ${media.large`
      margin-top: ${stripUnit(spacing.small) * 1.5 + 'px'};
    `};
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${spacing.large};

  ${media.large`
    padding-top: ${spacing.xLarge};
  `};
`

const Results = styled.span`
  font-size: ${fontSizes.small};
  color: ${black};
  font-weight: ${fontWeights.medium};
  display: block;
`

const PodsFilter = ({ value, onChange, options, results, isCompact }) => {
  const [expanded, setExpanded] = useState(false)
  const selectorButtonRef = useRef()

  const activeOption = options.find(opt => opt.value === value)

  const closeSelectorOnBlur = e => {
    if (selectorButtonRef.current && selectorButtonRef.current.contains(e.target)) return
    if (expanded) setExpanded(!expanded)
  }

  useEffect(() => {
    document.addEventListener('mousedown', closeSelectorOnBlur)
    return () => document.removeEventListener('mousedown', closeSelectorOnBlur)
  }, [expanded])

  return (
    <Container ref={selectorButtonRef}>
      <Wrap>
        <Inner>
          <Label isCompact={isCompact}>Filter by</Label>

          <Trigger
            isCompact={isCompact}
            onClick={() => {
              setExpanded(!expanded)
            }}
          >
            {activeOption.label}
          </Trigger>
          <IconWrap isCompact={isCompact}>
            <Chevron flipped={expanded}>
              <Icon icon={<DownChevronLarge />} />
            </Chevron>
          </IconWrap>
        </Inner>
        <Menu isCompact={isCompact} expanded={expanded}>
          {options.map((option, i) => {
            return (
              <MenuItem
                key={i}
                value={option.value}
                onClick={() => {
                  onChange(option.value)
                  setExpanded(false)
                }}
              >
                {option.label}
              </MenuItem>
            )
          })}
        </Menu>
      </Wrap>
      {results && (
        <Footer>
          <Results>{results} items</Results>
        </Footer>
      )}
    </Container>
  )
}

PodsFilter.propTypes = {
  options: array.isRequired,
  onChange: func.isRequired,
  value: oneOfType([string, number]),
  field: object,
  results: number,
  isCompact: bool
}

export default PodsFilter
